import { useAssertedParams } from '@folio/lib';
import { formatNumber } from '@folio/lib/number-formatting';
import { useTranslation } from 'react-i18next';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';

import { Badge, FunnelStepHeader, StepperFooter, useStepperContext } from '@folio/components';
import { useInvoicingFrequencyLabels } from '@folio/constants';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/forms/select';
import { SelectCards } from '../../components/forms/select-cards/SelectCards';
import {
  clientWithFork,
  configControllerGetPublicProductConfigQueryOptions,
  getNewBranchIdAfterFork,
  policyControllerGetPolicyQueryOptions,
  usePolicyControllerEditPolicy,
  viewSpecificControllerListPolicyCustomersQueryOptions,
  type ConfigBillingAssignmentDto,
  type PolicyDto,
} from 'korint-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

const DEFAULT_PAYER_KEY = '__default__';

function useAvailableConfigs(
  billingAssignmentConfig: {
    defaultConfig: ConfigBillingAssignmentDto;
    availableConfigs: ConfigBillingAssignmentDto[];
  },
  policy: PolicyDto,
  customerRoles: Set<string>,
) {
  return useMemo(() => {
    const allConfigs = billingAssignmentConfig.availableConfigs;
    const componentIdSet = new Set(policy.quote?.components.map(c => c.id) ?? []);

    return allConfigs.filter(config => {
      if (!customerRoles.has(config.defaultPayerRole)) return false;

      if (config.componentOverrides?.length) {
        const allOverridesValid = config.componentOverrides.every(
          override =>
            customerRoles.has(override.payerRole) && componentIdSet.has(override.componentId),
        );
        if (!allOverridesValid) return false;
      }

      return true;
    });
  }, [billingAssignmentConfig, policy.quote, customerRoles]);
}

/** Component IDs that appear in at least one config's componentOverrides */
function getOverriddenComponentIds(configs: ConfigBillingAssignmentDto[]): Set<string> {
  const ids = new Set<string>();
  for (const config of configs) {
    for (const override of config.componentOverrides ?? []) {
      ids.add(override.componentId);
    }
  }
  return ids;
}

/** Unique defaultPayerRole values across configs */
function getAvailableDefaultPayerRoles(configs: ConfigBillingAssignmentDto[]): string[] {
  return Array.from(new Set(configs.map(c => c.defaultPayerRole)));
}

/** Unique payer roles for an overridden component across configs */
function getAvailableOverrideRoles(
  configs: ConfigBillingAssignmentDto[],
  componentId: string,
): string[] {
  const roles = new Set<string>();
  for (const config of configs) {
    const override = config.componentOverrides?.find(o => o.componentId === componentId);
    roles.add(override?.payerRole ?? config.defaultPayerRole);
  }
  return Array.from(roles);
}

/** Find the config matching the selections (defaultPayerRole + per-override roles) */
function findMatchingConfig(
  configs: ConfigBillingAssignmentDto[],
  selections: Record<string, string>,
  overriddenComponentIds: Set<string>,
): ConfigBillingAssignmentDto | undefined {
  return configs.find(config => {
    if (config.defaultPayerRole !== selections[DEFAULT_PAYER_KEY]) return false;

    for (const componentId of overriddenComponentIds) {
      const override = config.componentOverrides?.find(o => o.componentId === componentId);
      const expectedRole = override?.payerRole ?? config.defaultPayerRole;
      if (expectedRole !== selections[componentId]) return false;
    }

    return true;
  });
}

type BillingAssignmentFunnelModuleProps = {
  title?: string;
  subtitle?: string;
  roleIcons?: Record<string, React.ComponentType<{ className?: string }>>;
};

export function BillingAssignmentFunnelModule({
  title,
  subtitle,
  roleIcons,
}: BillingAssignmentFunnelModuleProps = {}) {
  const { t } = useTranslation();
  const invoicingFrequencyLabels = useInvoicingFrequencyLabels();
  const { policyId, branchId } = useAssertedParams();
  const stepper = useStepperContext();
  const location = useLocation();
  const autoSkipTriggered = useRef(false);

  const {
    data: { productId },
  } = useSuspenseQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
  });

  const { data: billingAssignmentConfig } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
    select: ({ billing }) => billing.billingAssignment,
  });

  const { data: customersData, isFetching: isCustomersFetching } = useSuspenseQuery({
    ...viewSpecificControllerListPolicyCustomersQueryOptions(policyId, { branchId }),
  });

  const customerRoles = useMemo(
    () => new Set(customersData.data.map(c => c.role)),
    [customersData.data],
  );

  const {
    data: policy,
    refetch,
    isFetching: isPolicyFetching,
  } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
  });

  const { mutateAsync: updatePolicy, isPending: isUpdating } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const effectiveConfigs = useAvailableConfigs(
    billingAssignmentConfig,
    policy ?? ({} as PolicyDto),
    customerRoles,
  );

  const components = useMemo(
    () => policy?.proratedQuote?.components ?? policy?.quote?.components ?? [],
    [policy?.proratedQuote?.components, policy?.quote?.components],
  );

  const invoicingFrequency = policy?.invoicingConfig?.frequency ?? '';
  const frequencyLabel =
    invoicingFrequency in invoicingFrequencyLabels
      ? `/ ${invoicingFrequencyLabels[invoicingFrequency]}`
      : '';

  const overriddenComponentIds = useMemo(
    () => getOverriddenComponentIds(effectiveConfigs),
    [effectiveConfigs],
  );

  const mainComponents = useMemo(
    () => components.filter(c => !overriddenComponentIds.has(c.id)),
    [components, overriddenComponentIds],
  );

  const overriddenComponents = useMemo(
    () => components.filter(c => overriddenComponentIds.has(c.id)),
    [components, overriddenComponentIds],
  );

  const mainPrice = useMemo(
    () => mainComponents.reduce((sum, c) => sum + (c.premium?.amountWithFeesAndTaxes ?? 0), 0),
    [mainComponents],
  );

  // Build initial selections from current billing assignment or first config
  const initialSelections = useMemo(() => {
    const sels: Record<string, string> = {};
    if (effectiveConfigs.length === 0) return sels;

    const currentConfig = policy?.billingAssignment
      ? effectiveConfigs.find(c => c.id === policy.billingAssignment?.configId)
      : undefined;
    const baseConfig =
      currentConfig ??
      effectiveConfigs.find(c => c.id === billingAssignmentConfig.defaultConfig.id) ??
      effectiveConfigs[0];

    sels[DEFAULT_PAYER_KEY] = baseConfig.defaultPayerRole;
    for (const componentId of overriddenComponentIds) {
      const override = baseConfig.componentOverrides?.find(o => o.componentId === componentId);
      sels[componentId] = override?.payerRole ?? baseConfig.defaultPayerRole;
    }
    return sels;
  }, [effectiveConfigs, policy?.billingAssignment, overriddenComponentIds]);

  const [selections, setSelections] = useState<Record<string, string>>(initialSelections);

  useEffect(() => {
    setSelections(initialSelections);
  }, [initialSelections]);

  const matchedConfig = useMemo(
    () => findMatchingConfig(effectiveConfigs, selections, overriddenComponentIds),
    [effectiveConfigs, selections, overriddenComponentIds],
  );

  // Auto-skip when 0 or 1 config available
  // Wait for quote to be loaded before deciding — without components, filtering is incomplete
  // Also wait for any in-flight re-fetches to settle so we don't skip based on stale cache data
  const isBackNavigation = location.state?.navigationDirection === 'back';
  const isLoading = isPolicyFetching || isCustomersFetching;

  useEffect(() => {
    if (autoSkipTriggered.current || !policy || !policy.quote || isLoading) return;

    if (effectiveConfigs.length <= 1) {
      autoSkipTriggered.current = true;

      // When navigating backward, skip back to the previous step instead of forward
      if (isBackNavigation) {
        stepper.previous();
        return;
      }

      if (effectiveConfigs.length === 1 && !policy.billingAssignment) {
        updatePolicy({
          policyId,
          params: { branchId },
          data: { billingAssignment: { configId: effectiveConfigs[0].id } },
        })
          .then(response => {
            const newBranchId = getNewBranchIdAfterFork(response);
            stepper.next(newBranchId ? { branchId: newBranchId } : undefined);
          })
          .catch(() => {
            autoSkipTriggered.current = false;
          });
      } else {
        stepper.next();
      }
    }
  }, [
    effectiveConfigs,
    policy,
    branchId,
    policyId,
    stepper,
    updatePolicy,
    isBackNavigation,
    isLoading,
  ]);

  const handleSelectionChange = useCallback((key: string, role: string) => {
    setSelections(prev => ({ ...prev, [key]: role }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!matchedConfig) return;

    const isAlreadySet = policy?.billingAssignment?.configId === matchedConfig.id;
    let mutationBranchId: string | undefined = branchId;

    try {
      if (!isAlreadySet) {
        const response = await updatePolicy({
          policyId,
          params: { branchId },
          data: { billingAssignment: { configId: matchedConfig.id } },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }

      refetch();
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    } catch {
      // Error is surfaced by react-query's default error handling
    }
  }, [
    matchedConfig,
    policy?.billingAssignment?.configId,
    branchId,
    policyId,
    updatePolicy,
    refetch,
    stepper,
  ]);

  // Compute summary by payer
  const payerSummary = useMemo(() => {
    const summary: Record<string, number> = {};

    // Main components use the default payer
    const defaultRole = selections[DEFAULT_PAYER_KEY];
    if (defaultRole) {
      summary[defaultRole] = (summary[defaultRole] ?? 0) + mainPrice;
    }

    // Overridden components use their specific payer
    for (const component of overriddenComponents) {
      const role = selections[component.id];
      if (!role) continue;
      const amount = component.premium?.amountWithFeesAndTaxes ?? 0;
      summary[role] = (summary[role] ?? 0) + amount;
    }

    return Object.entries(summary);
  }, [selections, mainPrice, overriddenComponents]);

  if (effectiveConfigs.length <= 1 || !policy || !policy.quote || isUpdating || isLoading) {
    return <Loader2Icon className="size-8 animate-spin" />;
  }

  const defaultPayerRoles = getAvailableDefaultPayerRoles(effectiveConfigs);

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={title ?? t('funnelSteps.billingAssignment.title')}
        subtitle={subtitle ?? t('funnelSteps.billingAssignment.subtitle', '')}
      />
      <div className="rounded-lg border bg-card p-6">
        <Stack gap={6}>
          {/* "Formule principale" — all non-overridden components grouped */}
          <PayerSelect
            label={t('billingAssignment.mainComponentLabel')}
            price={mainPrice}
            frequencyLabel={frequencyLabel}
            value={selections[DEFAULT_PAYER_KEY] ?? ''}
            roles={defaultPayerRoles}
            productId={productId}
            roleIcons={roleIcons}
            onChange={role => handleSelectionChange(DEFAULT_PAYER_KEY, role)}
          />

          {/* Individual overridden components */}
          {overriddenComponents.map(component => {
            const roles = getAvailableOverrideRoles(effectiveConfigs, component.id);
            const price = component.premium?.amountWithFeesAndTaxes ?? 0;

            return (
              <PayerSelect
                key={component.id}
                label={t(
                  [
                    `billingAssignment.component.${component.id}_${productId}`,
                    `billingAssignment.component.${component.id}`,
                  ],
                  { defaultValue: component.id },
                )}
                price={price}
                frequencyLabel={frequencyLabel}
                value={selections[component.id] ?? ''}
                roles={roles}
                productId={productId}
                roleIcons={roleIcons}
                onChange={role => handleSelectionChange(component.id, role)}
              />
            );
          })}
        </Stack>

        {payerSummary.length > 0 && (
          <div className="mt-6 border-t pt-4">
            <div className="mb-2 text-sm font-medium">{t('billingAssignment.summary')}</div>
            <Stack gap={2}>
              {payerSummary.map(([role, amount]) => (
                <Group key={role} justify="between" gap={0}>
                  <Badge variant="secondary">
                    {t(
                      [
                        `billingAssignment.role.${role}_${productId}`,
                        `billingAssignment.role.${role}`,
                      ],
                      { defaultValue: role },
                    )}
                  </Badge>
                  <Text inline size="sm" weight="medium">
                    {formatNumber(amount / 100, 2, 2)} € {frequencyLabel}
                  </Text>
                </Group>
              ))}
            </Stack>
          </div>
        )}
      </div>

      <StepperFooter
        onNext={handleSubmit}
        nextButtonEnabled={!!matchedConfig}
        loading={isUpdating}
      />
    </Stack>
  );
}

function PayerSelect({
  label,
  price,
  frequencyLabel,
  value,
  roles,
  productId,
  roleIcons,
  onChange,
}: {
  label: string;
  price: number;
  frequencyLabel: string;
  value: string;
  roles: string[];
  productId: string;
  roleIcons?: Record<string, React.ComponentType<{ className?: string }>>;
  onChange: (role: string) => void;
}) {
  const { t } = useTranslation();

  const options = roles.map(role => ({
    value: role,
    label: t([`billingAssignment.role.${role}_${productId}`, `billingAssignment.role.${role}`], {
      defaultValue: role,
    }),
    icon: roleIcons?.[role],
  }));

  return (
    <div>
      <div className="mb-2 text-sm font-medium">
        {label}
        <Text inline weight="normal" tone="muted" className="ml-2">
          ({formatNumber(price / 100, 2, 2)} € {frequencyLabel})
        </Text>
      </div>
      {roleIcons ? (
        <SelectCards options={options} value={value} onChange={onChange} />
      ) : (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
